import { Type } from '@angular/core';
import { ActivatedRoute, ParamMap, Router } from '@angular/router';
import { NgbModal, NgbModalOptions, NgbModalRef } from '@ng-bootstrap/ng-bootstrap';
import { Subscription } from 'rxjs';


export interface DetailsModalDeepLinkOptions<TComponent, TContext> {
  router: Router;
  route: ActivatedRoute;
  modalService: NgbModal;

  /** The query parameter carrying the open record's ID, e.g. 'paymentDetailsId'. */
  queryParam: string;

  component: Type<TComponent>;

  modalOptions?: NgbModalOptions;

  /**
   * Set the modal's inputs. `context` is whatever the caller passed to `open`,
   * and is undefined for a deep-linked open, so the modal must be able to load
   * itself from `id` alone.
   */
  configure: (instance: TComponent, id: number, context?: TContext) => void;
}


const DEFAULT_MODAL_OPTIONS: NgbModalOptions = { size: 'lg', scrollable: true };


/**
 * Keeps a details modal and a query parameter in step, so the modal can be
 * deep-linked and shared. Instantiate one per modal on the host component
 * (several may coexist on a page) and drive it from the component's lifecycle.
 *
 * Reference behaviour: the participant details modal in
 * `event-participants-sub-list.component.ts` (PRO-1217).
 */
export class DetailsModalDeepLink<TComponent, TContext = void> {
  private readonly modalOptions: NgbModalOptions;

  private modalRef: NgbModalRef = null;
  private openId: number = null;
  private queryParamSubscription: Subscription = null;
  private suppressParamClear = false;

  constructor(private opts: DetailsModalDeepLinkOptions<TComponent, TContext>) {
    this.modalOptions = opts.modalOptions ?? DEFAULT_MODAL_OPTIONS;
  }

  /**
   * Begin matching the modal to the query parameter. A parameter already in the
   * URL opens the modal without rewriting the URL.
   */
  public start(): void {
    if (this.queryParamSubscription) {
      return;
    }
    this.queryParamSubscription = this.opts.route.queryParamMap.subscribe(
      params => { this.sync(this.readId(params)); }
    );
  }

  /** Stop matching and close the modal, leaving the URL alone. */
  public stop(): void {
    if (this.queryParamSubscription) {
      this.queryParamSubscription.unsubscribe();
      this.queryParamSubscription = null;
    }

    const modalRef = this.modalRef;
    // Drop the state first so the close handler treats this as a stale close
    // and does not navigate while the host is being destroyed.
    this.modalRef = null;
    this.openId = null;
    if (modalRef) {
      modalRef.dismiss();
    }
  }

  /** Open the modal for `id` and write the query parameter. */
  public open(id: number, context?: TContext): void {
    this.openModal(id, context, true);
  }

  /**
   * Tell the helper that the next close must not clear the query parameter,
   * because the modal's action has already navigated away and clearing would
   * race with that navigation.
   */
  public suppressNextParamClear(): void {
    this.suppressParamClear = true;
  }

  private readId(params: ParamMap): number | null {
    const rawValue = params.get(this.opts.queryParam);
    const parsed = rawValue ? parseInt(rawValue, 10) : NaN;
    return isNaN(parsed) ? null : parsed;
  }

  private sync(desiredId: number | null): void {
    if (desiredId === this.openId) {
      return;
    }

    if (desiredId === null) {
      if (this.modalRef) {
        this.modalRef.dismiss();
      }
      return;
    }

    this.openModal(desiredId, undefined, false);
  }

  private openModal(id: number, context: TContext | undefined, writeUrl: boolean): void {
    if (id === this.openId) {
      return;
    }

    // Switching directly from one record to another.
    if (this.modalRef) {
      this.modalRef.dismiss();
    }

    this.openId = id;

    if (writeUrl) {
      this.writeQueryParam(id);
    }

    const modalRef = this.opts.modalService.open(this.opts.component, this.modalOptions);
    this.modalRef = modalRef;

    this.opts.configure(modalRef.componentInstance, id, context);

    // The guard ignores a stale close after a different modal was opened.
    const handleClosed = () => {
      if (this.modalRef !== modalRef) {
        return;
      }
      this.modalRef = null;
      this.openId = null;
      if (this.suppressParamClear) {
        this.suppressParamClear = false;
      } else {
        this.writeQueryParam(null);
      }
    };
    modalRef.result.then(handleClosed, handleClosed);
  }

  private writeQueryParam(value: number | null): void {
    this.opts.router.navigate([], {
      relativeTo: this.opts.route,
      queryParams: { [this.opts.queryParam]: value },
      queryParamsHandling: 'merge',
      replaceUrl: true
    });
  }
}
